// src/components/SetupWizard/index.js

import React, { useState } from 'react';
import Review from './Review';
import { useSetupWizard } from '../../contexts/GlobalContext';
import { setupWizardApi, SetupConfig } from '../../utils/api/setupWizard';

const ApiUrlStep = ({ formData, setFormData, onNext, error }) => (
  <div className="p-6 space-y-6">
    <div>
      <h2 className="text-xl font-bold mb-6">Connect to API</h2>
    </div>
    {error && <div className="p-4 bg-red-100 border border-red-200 text-red-700 rounded">{error}</div>}
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium">API Base URL</label>
        <input
          type="text"
          value={formData.api_base_url}
          onChange={(e) => setFormData((prev) => ({ ...prev, api_base_url: e.target.value }))}
          placeholder="e.g. http://api.dashboard.skfx"
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <button
        onClick={onNext}
        disabled={!formData.api_base_url.trim()}
        className="w-full p-2 bg-blue-500 text-white rounded disabled:bg-blue-300"
      >
        Next
      </button>
    </div>
  </div>
);

const BasicInfo = ({ formData, setFormData, onNext }) => (
  <div className="p-6 space-y-6">
    <div>
      <h2 className="text-xl font-bold mb-6">Basic Brand Information</h2>
    </div>
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium">Company Name</label>
        <input
          type="text"
          value={formData.company_name}
          onChange={(e) => setFormData((prev) => ({ ...prev, company_name: e.target.value }))}
          placeholder="Enter company name"
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium">Primary Domain</label>
        <input
          type="text"
          value={formData.primary_domain}
          onChange={(e) => setFormData((prev) => ({ ...prev, primary_domain: e.target.value }))}
          placeholder="Enter primary domain"
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium">Tagline (Optional)</label>
        <input
          type="text"
          value={formData.tagline}
          onChange={(e) => setFormData((prev) => ({ ...prev, tagline: e.target.value }))}
          placeholder="Enter tagline"
          className="w-full p-2 border rounded"
        />
      </div>
      <button
        onClick={onNext}
        disabled={!formData.company_name || !formData.primary_domain}
        className="w-full p-2 bg-blue-500 text-white rounded disabled:bg-blue-300"
      >
        Next
      </button>
    </div>
  </div>
);

const VisualsTheme = ({ formData, setFormData, onBack, onNext }) => (
  <div className="p-6 space-y-6">
    <div>
      <h2 className="text-xl font-bold mb-6">Visuals & Theme</h2>
    </div>
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium">Logo URL</label>
        <input
          type="text"
          value={formData.logo_url}
          onChange={(e) => setFormData((prev) => ({ ...prev, logo_url: e.target.value }))}
          placeholder="Enter logo URL"
          className="w-full p-2 border rounded"
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium">Background URL</label>
        <input
          type="text"
          value={formData.background_url}
          onChange={(e) => setFormData((prev) => ({ ...prev, background_url: e.target.value }))}
          placeholder="Enter background URL"
          className="w-full p-2 border rounded"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium">Primary Color</label>
          <input
            type="color"
            value={formData.primary_color}
            onChange={(e) => setFormData((prev) => ({ ...prev, primary_color: e.target.value }))}
            className="w-full p-1 border rounded"
          />
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium">Secondary Color</label>
          <input
            type="color"
            value={formData.secondary_color}
            onChange={(e) => setFormData((prev) => ({ ...prev, secondary_color: e.target.value }))}
            className="w-full p-1 border rounded"
          />
        </div>
      </div>
      <div className="flex space-x-4">
        <button
          onClick={onBack}
          className="flex-1 p-2 border rounded hover:bg-gray-100"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Next
        </button>
      </div>
    </div>
  </div>
);

const AppConfig = ({ formData, setFormData, onBack, onNext }) => (
  <div className="p-6 space-y-6">
    <div>
      <h2 className="text-xl font-bold mb-6">App Configuration</h2>
    </div>
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium">API Base URL</label>
        <input
          type="text"
          value={formData.api_base_url}
          onChange={(e) => setFormData((prev) => ({ ...prev, api_base_url: e.target.value }))}
          placeholder="Enter API base URL"
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium">Default Locale</label>
        <select
          value={formData.default_locale}
          onChange={(e) => setFormData((prev) => ({ ...prev, default_locale: e.target.value }))}
          className="w-full p-2 border rounded"
        >
          <option value="en-US">English (US)</option>
          <option value="en-GB">English (UK)</option>
        </select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium">Support Email</label>
        <input
          type="email"
          value={formData.support_email}
          onChange={(e) => setFormData((prev) => ({ ...prev, support_email: e.target.value }))}
          placeholder="Enter support email"
          className="w-full p-2 border rounded"
          required
        />
      </div>
      <div className="flex space-x-4">
        <button
          onClick={onBack}
          className="flex-1 p-2 border rounded hover:bg-gray-100"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="flex-1 p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          disabled={!formData.api_base_url || !formData.support_email}
        >
          Next
        </button>
      </div>
    </div>
  </div>
);

export const SetupWizard = ({ onComplete }) => {
  const { updateSetupConfig, setConfigured, setApiBaseUrl, config } = useSetupWizard();
  const [step, setStep] = useState(config.apiBaseUrl ? 0 : -1);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    company_name: config.companyName || '',
    primary_domain: config.primaryDomain || '',
    tagline: config.tagline || '',
    logo_url: config.logoUrl || '',
    background_url: config.backgroundUrl || '',
    primary_color: config.primaryColor || '#000000',
    secondary_color: config.secondaryColor || '#ffffff',
    api_base_url: config.apiBaseUrl || '',
    default_locale: config.defaultLocale || 'en-US',
    support_email: config.supportEmail || '',
    experimental_features: config.experimentalFeatures || false
  });

  const handleApiUrlSubmit = async () => {
    setError(null);
    try {
      setApiBaseUrl(formData.api_base_url);
      // Try calling isConfigured now that we have an API URL
      const isSetup = await setupWizardApi.isConfigured();
      if (isSetup) {
        // Already configured, end wizard
        setConfigured(true);
        onComplete();
      } else {
        // Not configured, move to next step
        setStep(0);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to connect to the API. Please verify the URL and try again.');
    }
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      const configData = new SetupConfig(formData);
      configData.validate();

      const savedConfig = await setupWizardApi.saveConfig(configData);
      updateSetupConfig(savedConfig);
      setConfigured(true);
      onComplete();
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError(err.message || 'Failed to save configuration');
    }
  };

  // Steps after API URL is confirmed
  const steps = [
    <BasicInfo
      formData={formData}
      setFormData={setFormData}
      onNext={() => setStep(1)}
    />,
    <VisualsTheme
      formData={formData}
      setFormData={setFormData}
      onBack={() => setStep(0)}
      onNext={() => setStep(2)}
    />,
    <AppConfig
      formData={formData}
      setFormData={setFormData}
      onBack={() => setStep(1)}
      onNext={() => setStep(3)}
    />,
    <Review
      formData={formData}
      onBack={() => setStep(2)}
      onSubmit={handleSubmit}
      error={error ? { message: error } : null}
    />
  ];

  if (step === -1) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <ApiUrlStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleApiUrlSubmit}
            error={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        {error && step > -1 && (
          <div className="p-4 text-red-500 bg-red-100 border border-red-200 rounded">
            {error}
          </div>
        )}
        {steps[step]}
      </div>
    </div>
  );
};
